/**
 * @file Contains all core TypeScript types and interfaces for the application.
 * This serves as the single source of truth for data structures.
 */

import type { SegmentType } from '../utils/narrativeProgressSegments';
import type { EvidenceFind, IPScoreBreakdown } from './scoring';

/**
 * Game configuration interface for managing game settings
 */
export interface GameConfig {
  timer: number;
  minimumEvidence: number;
  endWhenAllFound: boolean;
  missClickPenalty: number;
  firstCaseId: string;
  videoSegmentLength: number; // Length of each video segment in seconds
  streakTimeThreshold: number; // Time in seconds to qualify for a streak
  timeBonusBase: number; // Base multiplier for time bonus calculation
  titleVideo?: TitleVideo; // Title video configuration
  ftueVideo?: FTUEVideo; // FTUE (First Time User Experience) video configuration
  showOnboardingVideo?: boolean; // Toggle to control if FTUE video is shown to players
  ftueVideoVersion?: number; // Version counter to invalidate client-side localStorage tracking
  ftueHighlightTimeoutSeconds?: number; // Time in seconds before highlighting first clue (default: 5)
  ftueArrowHighlightTimeoutSeconds?: number; // Time in seconds before highlighting navigation arrow (default: 3)
  ftueHintHighlightTimeoutSeconds?: number; // Time in seconds before highlighting hint button (default: 5)
  idleTimeoutSeconds: number; // Seconds of inactivity before tracking idle_timeout analytics event
  hintBaseCooldown: number; // Base cooldown duration in seconds for hints, grows by 1.25x each use
  parTime: number;
  aceTime: number;
  conversationBubbleAutoFadeMs: number;
}

export interface BaseVideo {
  id: string;
  url: string;
  uploadedAt?: number;
}

export interface TitleVideo extends BaseVideo {
  type: 'title';
}

export interface FTUEVideo extends BaseVideo {
  type: 'ftue';
}

export interface Coordinates {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Item {
  id: string;
  name: string;
  coords: Coordinates;
  found: boolean;
  conclusion?: string;
  // biome-ignore lint/suspicious/noExplicitAny: Generic game config allows arbitrary values
  startRect?: any; // Allow any type of rect for flexibility
  // Item-level locking - item requires another specific item to be found first
  unlockRequiredItemId?: string; // ID of item that must be found to unlock this item
  isKeyItem?: boolean; // Mark as key item for summary screen
  isLastItem?: boolean; // Mark as the last item to be shown in gameplay (only one per case, must be in last location)
  // Officer Dine popup message when this key item is found (string for single bubble, array for multi-bubble)
  dineMessage?: string | string[];
}

/**
 * Extends the Item interface to include scene and location information
 * Used for the evidence bucket system
 */
export interface ItemWithScene extends Item {
  sceneIndex: number; // Which scene this evidence belongs to
  sceneId: string; // ID of the scene this evidence belongs to
  locationIndex: number; // Which location this evidence belongs to
  locationId: string; // ID of the location this evidence belongs to
}

/**
 * Manages the evidence bucket for gameplay
 */
export interface EvidenceBucket {
  evidence: ItemWithScene[]; // All evidence with scene reference in randomized order
  activeClueIndices: number[]; // Indices of current active clues (up to 3)
  foundEvidence: ItemWithScene[]; // Evidence that has been found
  unlockedSceneIds: string[]; // IDs of scenes that have been unlocked
}

export interface LevelResult {
  locationIndex: number;
  locationName: string;
  score: number;
  timeBonus: number;
  accuracy: number;
  foundEvidenceCount: number;
  requiredEvidenceCount: number;
  totalEvidenceCount: number;
  foundItems: ItemWithScene[];
  highestSpeedStreak?: number;

  // Optional IP scoring fields (for backward compatibility)
  evidenceFinds: EvidenceFind[];
  ipBreakdown?: IPScoreBreakdown;
  isNewPersonalBest?: boolean;

  // Total completion time tracking
  totalCompletionTime?: number; // Time from first to last correct find (seconds)
  firstFindTimestamp?: number; // Timestamp of first correct find
  lastFindTimestamp?: number; // Timestamp of last correct find
}

export interface GameResult {
  totalScore: number;
  finalTimeBonus: number;
  overallAccuracy: number;
  levelResults: LevelResult[];
  foundItems: Item[];
  highestSpeedStreak?: number;
  totalEvidenceCount: number;

  // Optional IP scoring fields (for backward compatibility)
  totalIPBreakdown?: IPScoreBreakdown;
  isNewGlobalBest?: boolean;

  // Completion time and pace
  totalCompletionTime?: number; // Total time across all levels (seconds)
  paceMultiplier?: number; // Pace multiplier applied to score
  casePins?: number; // Case pins (0-5) based on time
}

export interface NarrativeItem {
  id: string;
  type: SegmentType;
  order: number;

  // For slides
  text?: string;
  imageUrl?: string;
  imagePrompt?: string;

  // For videos
  videoId?: string;
}

export interface Scene {
  id: string;
  name: string;
  imagePrompt?: string;
  imageUrl?: string;
  items: Item[]; // All items in the scene (no distinction between required/bonus)

  // Scene locking - scene requires a specific item to be found before it can be accessed
  unlockRequiredItemId?: string; // ID of item that must be found to unlock this scene
}

/**
 * Location-specific game configuration
 */
export interface LocationGameConfig {
  minimumEvidence?: number; // Minimum evidence required to complete this location (overrides level-based defaults)
  sequentialSceneUnlocking?: boolean; // When true, scenes unlock sequentially (all items in prior scene must be found)

  // Officer Dine Toast Messages
  officerDineToasts?: {
    countdown?: {
      message?: string; // Override for countdown message, use {requiredEvidenceCount} as placeholder
    };
    levelSummary?: {
      successMessage?: string; // Message when player passes the level
      failureMessage?: string; // Message when player fails the level
    };
    keyItemsDiscovered?: {
      message?: string; // Message when showing key items discovered screen
    };
  };

  // Dine popup message when all items found (replaces "LOCATION CLEARED" text)
  locationClearedMessage?: string;
}

/**
 * Represents a location (level) within a case
 */
export interface Location {
  id: string;
  name: string;
  description: string;
  narrativeSequence?: NarrativeItem[]; // Mixed slides and videos in sequence
  scenes: Scene[];
  gameConfig?: LocationGameConfig; // Location-specific game configuration
}

export interface VideoSegment {
  id: string;
  url: string; // Video URL (local or remote)
  title?: string; // Optional title/label for this segment
  duration?: number; // Duration in seconds (if known)
}

export interface CaseVideo extends BaseVideo {
  type: 'location' | 'ending' | 'title';
  locationId?: string;
  caseId?: string;
  segments?: VideoSegment[];
}

export interface EndingSlide {
  imageUrl: string;
  text: string;
}

export interface CaseData {
  caseId: string;
  title: string;
  description: string;
  successText: string;
  failText: string;
  suspect: {
    name: string;
    photoPrompt?: string;
    photoUrl?: string;
  };
  locations: Location[];
  videos?: CaseVideo[]; // Optional videos associated with the case
  videoSegmentLength?: number; // Length of video segments in seconds (optional, defaults to global config)
  endingType?: 'video' | 'slide'; // Whether the case ends with a video or slide (defaults to 'video' for backward compat)
  endingVideoId?: string; // ID of the video to play at case completion
  endingSlide?: EndingSlide; // Optional resolution narrative slide (image + text) at case completion
  titleVideo?: TitleVideo; // Case-specific video for discovery feed
  // Officer Dine Final Toast
  officerDineFinalToast?: string; // Message shown on case completion
  // Time-based scoring configuration
  parTime?: number; // Solid run target time in seconds (e.g., 115 for 1:55)
  aceTime?: number; // Top-tier run target time in seconds (e.g., 80 for 1:20)
}

export interface CaseInfo {
  caseId: string;
  title: string;
  description: string;
  suspectPhotoUrl?: string;
  suspectName?: string;
  titleVideoUrl?: string; // Case-specific video URL for discovery feed
  // Episode/Season fields
  seasonId?: string; // Which season this episode belongs to
  episodeNumber?: number; // 1, 2, 3... within the season
  requiredEpisodeId?: string; // caseId that must be completed first (null for Episode 1)
  releaseDate?: string; // ISO 8601 date (null if already released)
}

export interface Story {
  info: CaseInfo;
  getCaseData: () => CaseData;
}

/**
 * Daily content scheduling types for the /daily redirect route
 */
export interface DailyScheduleEntry {
  date: string; // "YYYY-MM-DD"
  caseId: string;
  label?: string; // For QA/metadata, e.g. "S1E1 - The Vanishing Artist"
}

export interface DailySchedule {
  entries: DailyScheduleEntry[];
  fallbackCaseId: string; // Used when no entry matches today
  timezone: string; // IANA timezone, e.g. "America/New_York"
  updatedAt: string; // ISO 8601
}

export enum GameStatus {
  TITLE,
  GENERATING,
  DEV_EDITOR,
  BRIEFING,
  FTUE_VIDEO,
  VIDEO_BEFORE_LOCATION,
  NARRATIVE,
  GAMEPLAY,
  LEVEL_ENDING,
  KEY_ITEMS_DISCOVERED,
  LEVEL_SUMMARY,
  LEVEL_TRANSITION,
  ENDING_VIDEO,
  SLIDE_GAME_ENDING,
  FINAL_SUMMARY,
  ERROR,
}

export enum DisplayContext {
  EDITOR = 'editor',
  GAMEPLAY = 'gameplay',
}

export enum ScreenSize {
  MOBILE = 'mobile',
  TABLET = 'tablet',
  DESKTOP = 'desktop',
}

/**
 * Object-fit modes for image display
 */
export enum ObjectFitMode {
  CONTAIN = 'contain',
  COVER = 'cover',
}

export interface AnimationStartRect {
  left: number;
  top: number;
  width: number;
  height: number;
  imageWidth: number;
  imageHeight: number;
  imageOffsetX: number;
  imageOffsetY: number;
  // Additional properties for better positioning
  scaleX?: number;
  scaleY?: number;
  offsetX?: number;
  offsetY?: number;
  renderedWidth?: number;
  renderedHeight?: number;
  scaleFactor?: number;
  // Viewport coordinates for portal-based animations (position: fixed)
  viewportLeft?: number;
  viewportTop?: number;
}

export type AnimationTargetType = 'default' | 'thumbnail' | 'evidence-count';

export interface AnimatingItemData extends Item {
  startRect: AnimationStartRect;
  targetType?: AnimationTargetType;
  targetPosition?: { left: number; top: number };
  targetSize?: { width: number; height: number };
}
